import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../utils/axios';

const AuthContext = createContext(null);

/**
 * Keeps the authenticated B2B customer profile in React state.
 * Hydrates from GET /b2b/auth/me on first mount. Login writes the
 * cookie server-side (httpOnly), so the only thing we persist client-
 * side is the profile object in memory.
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const hydrate = useCallback(async () => {
        try {
            const { data } = await api.get('/b2b/auth/me');
            setUser(data.user);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { hydrate(); }, [hydrate]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post('/b2b/auth/login', { email, password });
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try { await api.post('/b2b/auth/logout'); } catch {}
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refresh: hydrate }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
